import { BehaviorSubject, combineLatest, Observable } from 'rxjs'
import {
  map,
  switchMap,
  take
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { Component, OnInit } from '@angular/core'
import { RequestQueryBuilder } from '@nestjsx/crud-request'
import { Store } from '@ngrx/store'
import { BulkAction, BulkActionEvent } from 'src/app/shared/interfaces'
import { NotificationService } from 'src/app/core/notification.service'
import { UntilDestroy } from '@ngneat/until-destroy'
import { Course } from '../course'
import { courseActions } from '../course.actions'
import { courseSelectors } from '../course.selectors'
import { CourseService } from '../course.service'
import { AccessRights } from '../../role/user-role/user-role'
import { ActivatedRoute, Router } from '@angular/router'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'
import { Answer } from '../../survey/answer/answer'
import { bookingActions } from '../../booking/booking.actions'
import { bookingSelectors } from '../../booking/booking.selectors'
import { participationSelectors } from '../../booking/participation/participation.selectors'
import { Booking } from '../../booking/booking'
import {
  Participation,
  ParticipationStatus
} from '../../booking/participation/participation'
import {
  AchievementAdjustPayload,
  AchievementRow
} from 'src/app/shared/components/achievement-table/table.component'
import { AdjustAchievementAnswersModalComponent } from './adjust-answers-modal.component'
import { Question } from '../../survey/question/question'

@UntilDestroy()
@Component({
  selector: 'course-achievement-tab',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class CourseAchievementTabComponent implements OnInit {
  public accessRights = AccessRights
  public course$: Observable<Course>
  public courseId: number
  public isLoading$: Observable<boolean>
  public achievementSurveyAnswers$: Observable<Answer[]>
  public participantMemberIds$: Observable<number[]>
  public achievementQuestions$: Observable<Question[]>
  public achievementQuestionIds$: Observable<number[]>

  public BulkAction = BulkAction
  private didFetchCourse = false
  private fetchedBookingsCourseIds = new Set<number>()
  public isInModal: boolean = false
  private refreshAnswers$ = new BehaviorSubject<number>(0)

  constructor(
    private store: Store<AppState>,
    private notificationService: NotificationService,
    private activeRoute: ActivatedRoute,
    public router: Router,
    private modalService: BsModalService,
    private courseService: CourseService
  ) {}

  ngOnInit() {
    this.isInModal = this.activeRoute.outlet === 'modal'

    this.courseId = Number(this.activeRoute.snapshot.params['courseId'])

    if (!this.didFetchCourse) {
      this.store.dispatch(
        courseActions.fetchOneById({
          id: this.courseId,
          params: RequestQueryBuilder.create({
            join: [{ field: 'product' }, { field: 'product.translations' }]
          })
        })
      )

      this.didFetchCourse = true
    }

    if (!this.fetchedBookingsCourseIds.has(this.courseId)) {
      this.store.dispatch(
        bookingActions.fetchMany({
          params: RequestQueryBuilder.create({
            search: { courseId: this.courseId },
            join: [{ field: 'participations' }]
          })
        })
      )
      this.fetchedBookingsCourseIds.add(this.courseId)
    }

    this.course$ = this.store.select(courseSelectors.selectEntities).pipe(
      map((entities) => entities[this.courseId])
    )

    this.achievementSurveyAnswers$ = this.refreshAnswers$.pipe(
      switchMap(() =>
        this.courseService.getAchievementSurveyAnswers(this.courseId)
      )
    )
    this.achievementQuestions$ =
      this.courseService.getAchievementSurveyQuestions(this.courseId)
    this.achievementQuestionIds$ = this.achievementQuestions$.pipe(
      map((questions) => questions.map((question) => question.id))
    )

    this.participantMemberIds$ = combineLatest([
      this.course$,
      this.store.select(bookingSelectors.selectAll),
      this.store.select(participationSelectors.selectAll)
    ]).pipe(
      map(([course, bookings, participations]) => {
        const courseBookingIds = bookings
          .filter((booking: Booking) => booking.courseId === course?.id)
          .map((booking: Booking) => booking.id)

        const uniqueMemberIds = new Set<number>()
        participations
          .filter(
            (participation: Participation) =>
              courseBookingIds.includes(participation.bookingId) &&
              participation.status === ParticipationStatus.ACCEPTED
          )
          .forEach((participation: Participation) => {
            if (participation.memberId) {
              uniqueMemberIds.add(participation.memberId)
            }
            if (participation.invitedMemberId) {
              uniqueMemberIds.add(participation.invitedMemberId)
            }
          })

        return Array.from(uniqueMemberIds)
      })
    )
  }

  public onOpenAdjust(payload: AchievementAdjustPayload): void {
    const modalRef = this.modalService.show(
      AdjustAchievementAnswersModalComponent,
      {
        class: 'modal-lg',
        initialState: {
          courseId: this.courseId,
          memberId: payload.memberId,
          memberName: payload.memberName,
          questions: payload.questions,
          answersByQuestionId: payload.answersByQuestionId
        }
      }
    )

    modalRef.content.saved?.pipe(take(1)).subscribe(() => {
      this.refreshAnswers$.next(Date.now())
    })
  }

  public onBulkAction(bulkActionEvent: BulkActionEvent): void {
    if (!bulkActionEvent.items?.length) {
      return
    }

    this.achievementQuestions$.pipe(take(1)).subscribe((questions) => {
      const isAllMembersAction =
        bulkActionEvent.action === BulkAction.SET_ALL_TO_ACHIEVED

      const modalRef = this.modalService.show(
        AdjustAchievementAnswersModalComponent,
        {
          class: 'modal-lg',
          initialState: {
            courseId: this.courseId,
            memberName: '',
            isBulkMode: true,
            bulkMode: isAllMembersAction ? 'all' : 'selected',
            bulkMembers: (bulkActionEvent.items as AchievementRow[]).map(
              (row) => ({
                memberId: row.memberId,
                memberName: row.memberName,
                answersByQuestionId: row.answersByQuestionId
              })
            ),
            questions,
            answersByQuestionId: {}
          }
        }
      )

      modalRef.content.saved?.pipe(take(1)).subscribe(() => {
        this.refreshAnswers$.next(Date.now())
      })
    })
  }
}
